/*
 * Cart rewards meter
 *
 * Shows how close the cart is to free shipping and then to the free gift, in
 * the pull-out cart and on the cart page. Both thresholds are settings on the
 * Cart drawer section in the theme editor.
 *
 * It renders from JavaScript because the theme draws the cart in two separate
 * templates and replaces their markup wholesale on every change; watching for
 * that and re-inserting is far less invasive than editing both templates.
 */

(function () {
  'use strict';

  var SETTINGS = window.RoutineRewardsSettings || {};

  var SHIPPING = Number(SETTINGS.shipping || 0);
  var GIFT = Number(SETTINGS.gift || 0);
  var MONEY = SETTINGS.moneyFormat || '${{amount}}';
  var TEXT = SETTINGS.text || {};

  /* Where the cart is drawn: the pull-out cart's body, and the cart page's
     summary. The drawer is addressed by its type, because every other drawer in
     this theme — search, filters, options — uses .drawer__body too. */
  var HOSTS = '[drawer-type="cart-drawer"] .drawer__body, .cart-table__checkoutWrp';

  function formatMoney(cents) {
    var value = Number(cents || 0);

    function withDecimals(precision, thousands, decimal) {
      var fixed = (value / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return MONEY.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return withDecimals(0, ',', '.');
        case 'amount_with_comma_separator':
          return withDecimals(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDecimals(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDecimals(2, "'", '.');
        default:
          return withDecimals(2, ',', '.');
      }
    });
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function label(key, fallback) {
    return TEXT[key] || fallback;
  }

  /*
   * The line above the meter: whichever reward is next, or both earned.
   * [amount] in the wording is replaced with what is still to spend.
   */
  function message(total) {
    var wording;
    var remaining;

    if (SHIPPING && total < SHIPPING) {
      wording = label('shipping_to_go', "You're [amount] away from free shipping");
      remaining = SHIPPING - total;
    } else if (GIFT && total < GIFT) {
      wording = label('gift_to_go', 'Free shipping unlocked. [amount] away from your free gift');
      remaining = GIFT - total;
    } else {
      return escapeHtml(label('all_earned', 'Free shipping and a free gift unlocked'));
    }

    return escapeHtml(wording).replace('[amount]', '<b>' + escapeHtml(formatMoney(remaining)) + '</b>');
  }

  function markup(total) {
    var goal = GIFT || SHIPPING;
    var filled = goal ? Math.min(Math.round((total / goal) * 100), 100) : 0;
    var shippingAt = goal && SHIPPING ? Math.min(Math.round((SHIPPING / goal) * 100), 100) : 0;

    var shippingWon = SHIPPING && total >= SHIPPING;
    var giftWon = GIFT && total >= GIFT;

    return (
      '<p class="routine-rewards__message">' + message(total) + '</p>' +
      '<div class="routine-rewards__track" role="presentation">' +
        '<span class="routine-rewards__fill" style="width: ' + filled + '%"></span>' +
        (SHIPPING && GIFT
          ? '<span class="routine-rewards__pip" style="left: ' + shippingAt + '%"></span>'
          : '') +
      '</div>' +
      '<ul class="routine-rewards__tiers">' +
        (SHIPPING
          ? '<li class="routine-rewards__tier' + (shippingWon ? ' is-earned' : '') + '">' +
              escapeHtml(label('shipping_tier', 'Free shipping')) +
              ' <span>' + escapeHtml(formatMoney(SHIPPING)) + '</span></li>'
          : '') +
        (GIFT
          ? '<li class="routine-rewards__tier' + (giftWon ? ' is-earned' : '') + '">' +
              escapeHtml(label('gift_tier', 'Free gift')) +
              ' <span>' + escapeHtml(formatMoney(GIFT)) + '</span></li>'
          : '') +
      '</ul>'
    );
  }

  /*
   * Painting is watched for by the observer below, so writing the same markup
   * over again would wake it, which would paint again, and the meter would sit
   * there fetching the cart every quarter second forever. Write only when what
   * is on screen has actually changed.
   */
  function paint(total) {
    var html = markup(total);

    Array.prototype.forEach.call(document.querySelectorAll(HOSTS), function (host) {
      var panel = host.querySelector('[data-routine-rewards]');

      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'routine-rewards';
        panel.setAttribute('data-routine-rewards', '');
        host.insertBefore(panel, host.firstChild);
      }

      if (panel.innerHTML !== html) panel.innerHTML = html;
    });

    hideThemeBar();
  }

  /*
   * The theme has its own free-shipping bar, which says the same thing about
   * shipping and nothing about the gift. Once this meter is on screen that bar
   * is a duplicate, so it is hidden here rather than in the stylesheet: if this
   * ever fails to draw, the theme's bar is still there.
   */
  function hideThemeBar() {
    Array.prototype.forEach.call(document.querySelectorAll('free-shipping'), function (bar) {
      var wrapper = bar.closest('.cart__free__shipping__alignment') || bar;
      wrapper.style.setProperty('display', 'none', 'important');
    });
  }

  var inFlight = false;
  var again = false;

  /*
   * total_price is what the customer pays for the items, so every discount
   * already taken off counts towards the next reward.
   */
  function paintCart(cart) {
    if (!cart || typeof cart.total_price === 'undefined') return false;
    paint(Number(cart.total_price || 0));
    return true;
  }

  /*
   * A change that arrives while the cart is being fetched used to be dropped,
   * which left the meter showing the cart as it was one change ago - the whole
   * of the lag. It is remembered and run instead.
   */
  function refresh() {
    if (!SHIPPING && !GIFT) return;
    if (!document.querySelector(HOSTS)) return;

    if (inFlight) {
      again = true;
      return;
    }

    inFlight = true;

    fetch(root() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(paintCart)
      .catch(function () {
        /* leave whatever is on screen */
      })
      .finally(function () {
        inFlight = false;
        if (again) {
          again = false;
          refresh();
        }
      });
  }

  var timer = null;

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 250);
  }

  /* The drawer is rendered by a section further down the page, so on a slow
     paint it can arrive after this runs. Look again a few times before giving
     up rather than waiting on a cart change that may never come. */
  function waitForHost(attempt) {
    if (document.querySelector(HOSTS)) {
      refresh();
      return;
    }

    if (attempt >= 10) return;

    setTimeout(function () {
      waitForHost(attempt + 1);
    }, 500);
  }

  function start() {
    waitForHost(0);

    /* The theme redraws the cart in place and its markup is replaced whole, so
       watch the wrappers that stay put rather than the markup inside them. */
    var observer = new MutationObserver(schedule);
    var section = document.getElementById('shopify-section-cart-drawer');
    var page = document.querySelector('.cart-table__wrp, .cart-table__container');

    if (section) observer.observe(section, { childList: true, subtree: true });
    if (page) observer.observe(page, { childList: true, subtree: true });

    if (window._EVENT_HELPER && window._EVENT_HELPER.triggerCartChange) {
      document.addEventListener(window._EVENT_HELPER.triggerCartChange, schedule);
    }

    /*
     * The surest signal of the three. routine-cart-open.js watches every
     * request that writes to the cart - adds, quantity changes, removals - and
     * announces it with the cart the response carried. Painting from that is
     * immediate: no debounce to wait out, and no second request to ask for a
     * cart that has already been handed over. The observer and the theme's own
     * event stay as backstops for anything that changes the cart another way.
     */
    document.addEventListener('routine:cart', function (event) {
      var cart = event && event.detail && event.detail.cart;
      if (paintCart(cart)) return;
      schedule();
    });
  }

  window.RoutineRewards = { refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
